import type { ComponentProps, ReactElement, ReactNode } from 'react';

import { Slot } from 'radix-ui';

import { BrandMark } from '@/components/brand/BrandMark.js';
import { cn } from '@/lib/utils.js';
import { PageShell } from './PageShell.js';

/**
 * Тёмная «ink»-шапка нового макета. Стоит на семи артбордах из девяти: лендинг, вход,
 * кабинет, поддержка, экраны-исходов, юридический документ, 404.
 *
 * До редизайна её роль исполняли три несогласованные копии — `LandingHeader`,
 * `DashboardHeader` и обёртки `BrandLockup` на экранах-исходах, — и высота у них
 * совпадала случайно.
 *
 * ⚠️ Шапка НЕ знает, что в ней стоит. Навигация и действия приходят слотами `nav` и
 * `actions`: иначе общий компонент пришлось бы учить всем маршрутам продукта, включая
 * админские, и он перестал бы быть общим. Проп `variant` здесь описывает ГЕОМЕТРИЮ
 * (высота, выравнивание), а не страницу.
 *
 * ⚠️ `sticky`, а не `fixed`. Прежний `LandingHeader` был `fixed` и выпадал из потока,
 * из-за чего первый экран нёс компенсирующий `padding-top: 100px` — цифру, которую
 * нельзя менять, не помня про шапку. `sticky` держит место сам; якорям секций всё равно
 * нужен `scroll-mt`, потому что шапка перекрывает верх прокрученного блока.
 */
type HeaderVariant = 'default' | 'compact';

interface SiteHeaderProps {
  /** Центральная навигация. На мобильном скрывается — в макете её там нет. */
  nav?: ReactNode;
  /** Правая группа: кнопки входа, счётчик дней, меню аккаунта. */
  actions?: ReactNode;
  /** `compact` — экраны-исходы: 54px и логотип у края, без колонки 1200px. */
  variant?: HeaderVariant;
  /** Логотип по центру — мобильный артборд экрана входа. */
  centered?: boolean;
  sticky?: boolean;
  className?: string;
}

export function SiteHeader({
  nav,
  actions,
  variant = 'default',
  centered = false,
  sticky = false,
  className,
}: SiteHeaderProps): ReactElement {
  const row = (
    <div
      className={cn(
        'flex items-center',
        /**
         * ⚠️ Высота берётся из токена `--mm-header-h`, а не литералом. Раньше про одну
         * и ту же шапку в коде жили пять несвязанных чисел (54/60 здесь, 72 в
         * `scroll-mt` секций и в наблюдателе, 84 на юр.страницах, 60 в e2e), и их
         * расхождение проявлялось как промах подсветки в меню лендинга.
         *
         * `compact` остаётся литеральным: у экранов-исходов высота одна на всех ширинах
         * и с ритмом лендинга не связана.
         */
        variant === 'compact' ? 'h-[54px]' : 'h-(--mm-header-h)',
        centered ? 'justify-center' : 'justify-between gap-6',
      )}
    >
      <BrandMark />

      {/* Навигация прячется до md: на мобильных артбордах её нет ни на одном экране. */}
      {nav !== undefined && (
        <nav className="hidden items-center gap-6 text-meta font-medium md:flex lg:gap-[26px]">
          {nav}
        </nav>
      )}

      {actions !== undefined && <div className="flex items-center gap-4">{actions}</div>}
    </div>
  );

  return (
    <header
      className={cn(
        'w-full bg-mm-ink text-white',
        sticky && 'sticky top-0 z-40',
        // Без обнуления шапка наследует подчёркивание ссылок страницы.
        '[&_a]:no-underline',
        /**
         * ⚠️ Кольцо фокуса внутри шапки БЕЛОЕ. Глобальное правило в `index.css` красит его
         * в `--ring`, а это `--mm-ink` — тот же цвет, что и фон шапки. То есть переход по
         * Tab внутри неё был не «плохо заметен», а невидим вовсе: человек с клавиатурой
         * терял место. Правило локальное, потому что дело в фоне, а не в самом кольце.
         */
        '[&_:focus-visible]:outline-white',
        className,
      )}
    >
      {variant === 'compact' ? <div className="px-5 md:px-7">{row}</div> : <PageShell>{row}</PageShell>}
    </header>
  );
}

/**
 * Пункт навигации в шапке. Активный подчёркнут жёлтым — единственное место продукта,
 * где акцентный цвет логотипа работает указателем.
 *
 * ⚠️ Подчёркивание — не единственный признак активного пункта: он же становится белым
 * против полупрозрачного. Одного цвета не хватило бы при дальтонизме, одного
 * подчёркивания — на жёлтом по тёмному оно едва заметно.
 */
export function SiteHeaderLink({
  active = false,
  asChild = false,
  className,
  children,
  ...props
}: { active?: boolean; asChild?: boolean } & ComponentProps<'a'>): ReactElement {
  /**
   * `asChild` нужен, потому что в шапке соседствуют два вида ссылок: якорь секции
   * лендинга (обычный `<a href="#…">`) и переход по маршруту (`<Link>` роутера).
   * Без слота пришлось бы либо дублировать компонент, либо отдавать роутеру голый
   * `<a>` — то есть перезагружать SPA на каждом клике по «Կապ».
   */
  const Comp = asChild ? Slot.Root : 'a';

  return (
    <Comp
      aria-current={active ? 'page' : undefined}
      className={cn(
        'transition-colors',
        active ? 'border-b-2 border-mm-accent pb-0.5 text-white' : 'text-white/70 hover:text-white',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}
